'use client'
import React, { useMemo } from 'react';
import { useTranslations } from "next-intl";
import { type Blog } from '@/lib/@types/api/blog';
import { Link } from "@/navigation";

interface CategoriesProps {
	clsList: Blog.BlogCl[];
	isShowcount?: boolean
}

const Categories: React.FC<CategoriesProps> = ({ clsList, isShowcount = true }) => {
	const t = useTranslations();

	// 递归查找所有子分类 ID
	const getAllSubCategoryIds = (parentId: number, categories: Blog.BlogCl[]): number[] => {
		const directChildren = categories.filter(cat => cat.cls_parent === parentId);
		const allIds = [parentId];
		directChildren.forEach(child => {
			allIds.push(child.cls_id);
			const grandchildren = getAllSubCategoryIds(child.cls_id, categories);
			allIds.push(...grandchildren.filter(id => id !== child.cls_id));
		});
		return allIds;
	};

	// 过滤掉所有 applications 相关的分类
	const filteredCategories = useMemo(() => {
		const applicationsCategory = clsList.find(cat => cat.cls_slug === "applications");
		const applicationsSubCategoryIds = applicationsCategory 
			? getAllSubCategoryIds(applicationsCategory.cls_id, clsList)
			: [];
		
		return clsList
			.filter(cat => !applicationsSubCategoryIds.includes(cat.cls_id) && cat.cls_slug !== "applications")
			.sort((a, b) => a.cls_id - b.cls_id); // 按 ID 排序
	}, [clsList]);

	return (
		<>
		<style jsx>{`
			.elegant-scrollbar::-webkit-scrollbar {
				width: 6px;
			}
			.elegant-scrollbar::-webkit-scrollbar-track {
				background: #f1f1f1;
				border-radius: 10px;
			}
			.elegant-scrollbar::-webkit-scrollbar-thumb {
				background: rgba(4, 27, 78, 0.3);
				border-radius: 10px;
			}
			.elegant-scrollbar::-webkit-scrollbar-thumb:hover {
				background: rgba(5, 19, 50, 0.5);
			} 
		`}</style>
			<div className="overflow-hidden border border-gray-200 bg-white shadow-md">
				<div className="bg-mainColor px-5 py-3">
					<h2 className="text-lg font-bold text-white flex items-center gap-2">
						<i className="ri-folder-line text-lg"></i>
						{t('blog.Categories')}
					</h2>
				</div>
				<div className="list-cate max-h-[500px] max-md:max-h-[300px] pr-2 overflow-y-auto p-4 space-y-2 elegant-scrollbar">
				{filteredCategories.length > 0 && filteredCategories.map((item) => (
					<Link 
						href={`/category/${item.cls_slug}`}
						key={item.cls_id}
						className="flex items-center justify-between cursor-pointer py-3 px-4 rounded-md bg-white border border-gray-200 hover:border-mainColor hover:text-mainColor transition-all duration-200 group"
					>
						<div className="flex items-center justify-between w-full">
							<h3 className="capitalize text-gray-700 group-hover:text-mainColor font-medium">{item.cls_name}</h3>
							<div className="flex items-center gap-3">
								{isShowcount && (
									<div className="text-sm text-gray-500 group-hover:text-mainColor">({item.cls_related_count})</div>
								)}
								<svg className="w-4 h-4 text-gray-400 group-hover:text-mainColor transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
								</svg>
							</div>
						</div>
					</Link>
				))}
			</div>
		</div>
		</>
	);
};

export default Categories;
